import { useCallback, useEffect, useRef, useState } from 'react';

import { useLoginState } from '@xing-com/hub';

type TcfListenerId = number | false;

type TcfData = {
  listenerId?: TcfListenerId;
  vendor: {
    consents: Record<number, boolean | undefined>;
  };
};

type TcfEventCallback = (tcData: TcfData, success: boolean) => void;

type TcfApi = (
  command: 'addEventListener' | 'removeEventListener',
  version: 2,
  callback: TcfEventCallback,
  listenerId?: TcfListenerId
) => void;

declare global {
  interface Window {
    __tcfapi?: TcfApi;
  }
}

// https://usercentrics.com/docs/web/features/events/uc-ui-cmp-event/
type EventDetail = {
  source: 'none' | 'button' | 'first' | 'second' | 'embeddings' | '__ucCmp';
  type:
    | 'CMP_SHOWN'
    | 'ACCEPT_ALL'
    | 'DENY_ALL'
    | 'SAVE'
    | 'MORE_INFORMATION_LINK'
    | 'IMPRINT_LINK'
    | 'PRIVACY_POLICY_LINK';
};

const YIELDLOVE_VENDOR_ID = 251; // YieldLove GmbH

export const useDisplayAds = (): { displayAdsAllowed: boolean } => {
  const isLoggedOut = useLoginState() === 'LOGGED_OUT';
  const [cmpInitialized, setCmpInitialized] = useState(false);
  const [hasMarketingConsent, setHasMarketingConsent] = useState(false);
  const initialTcfRun = useRef(true);
  const listenerId = useRef<TcfListenerId>(false);

  const isDebugMode =
    globalThis.localStorage?.getItem('xing_display_ads_debug') === 'true';

  const displayAdsAllowed =
    (isLoggedOut && isDebugMode) || (isLoggedOut && hasMarketingConsent);

  const handleCmpInitializeEvent = useCallback((): void => {
    setCmpInitialized(true);
  }, []);

  const handleCmpEvent = useCallback((event: Event): void => {
    const customEvent = event as CustomEvent<EventDetail>;
    const eventType = customEvent.detail?.type;

    if (eventType === 'ACCEPT_ALL') {
      setHasMarketingConsent(true);
    }

    if (eventType === 'DENY_ALL') {
      setHasMarketingConsent(false);
    }
  }, []);

  useEffect(() => {
    globalThis.addEventListener('UC_UI_INITIALIZED', handleCmpInitializeEvent);
    globalThis.addEventListener('UC_UI_CMP_EVENT', handleCmpEvent);

    return () => {
      globalThis.removeEventListener(
        'UC_UI_INITIALIZED',
        handleCmpInitializeEvent
      );
      globalThis.removeEventListener('UC_UI_CMP_EVENT', handleCmpEvent);
    };
  }, [handleCmpInitializeEvent, handleCmpEvent]);

  const handleTcfEvent = useCallback(
    (tcData: TcfData, success: boolean): void => {
      if (success) {
        const {
          listenerId: tcfListenerId,
          vendor: { consents },
        } = tcData;

        // Could be undefined when first initialized without user choice.
        listenerId.current = tcfListenerId || false;
        setHasMarketingConsent(consents[YIELDLOVE_VENDOR_ID] || false);
      } else {
        setHasMarketingConsent(false);
      }
    },
    []
  );

  /**
   * Vendor consent needs to be determined via IAB TCF vendor framework.
   * https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/tree/master/TCFv2
   * see: IAB Tech Lab - CMP API v2.md
   */
  useEffect(() => {
    const tcfApi = window.__tcfapi;
    const shouldInit = cmpInitialized && initialTcfRun.current && tcfApi;

    if (shouldInit) {
      tcfApi('addEventListener', 2, handleTcfEvent);
      initialTcfRun.current = false;
    }

    return () => {
      if (shouldInit) {
        tcfApi('removeEventListener', 2, handleTcfEvent, listenerId.current);
      }
    };
  }, [cmpInitialized, handleTcfEvent]);

  return {
    displayAdsAllowed,
  };
};
